import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRight, Search, ShieldAlert, UsersRound } from 'lucide-react';
import type { UseQueryResult } from '@tanstack/react-query';
import type { IntegrityResponse, ItemAnalysisResponse, PeopleResponse, TestsResponse } from 'modules/analytics';
import type { AnalyticsFilters } from 'pages/Analytics/useAnalyticsFilters';
import { StatTile } from 'components/ui/stat-tile';
import { StatusBadge } from 'components/ui/status-badge';
import { ListCard, M, MobileEmpty, MobileError, MobileSkeleton, Monogram, scoreColor } from 'components/mobile';

/** The advanced (PRO) phone views: people, tests & questions, integrity. */

export type Query<T> = Pick<UseQueryResult<T>, 'data' | 'isLoading' | 'isError' | 'error' | 'refetch'>;

/** A failed load keeps its view name so a retry targets the right query. */
export function RetryPanel({ scope, error, onRetry }: { scope: string; error?: unknown; onRetry: () => void }) {
  return (
    <div data-mobile-analytics-error={scope}>
      <MobileError description={error instanceof Error ? error.message : undefined} onRetry={onRetry} />
    </div>
  );
}

function initials(name: string) {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="mb-2 text-[14px] font-semibold" style={{ color: M.ink }}>
      {children}
    </h2>
  );
}

export function PeopleTab({ people, filters }: { people: Query<PeopleResponse>; filters: AnalyticsFilters }) {
  const { t } = useTranslation();
  const { q, setQ } = filters;
  const [term, setTerm] = useState(q);
  useEffect(() => {
    setTerm(q);
  }, [q]);
  useEffect(() => {
    const timeout = window.setTimeout(() => {
      if (term !== q) setQ(term);
    }, 350);
    return () => window.clearTimeout(timeout);
  }, [q, setQ, term]);

  if (people.isLoading) return <MobileSkeleton rows={5} />;
  if (people.isError) return <RetryPanel scope="people" error={people.error} onRetry={() => void people.refetch()} />;
  const rows = people.data?.rows ?? [];
  const currentPage = filters.page;
  const totalPages = Math.max(1, Math.ceil((people.data?.total ?? 0) / (people.data?.perPage ?? 25)));

  return (
    <div className="space-y-3 px-4 pb-2 pt-3">
      <label className="relative block">
        <span className="sr-only">{t('analytics_people_search')}</span>
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: M.faint }} aria-hidden />
        <input
          value={term}
          onChange={(event) => setTerm(event.target.value)}
          placeholder={t('analytics_people_search')}
          className="h-11 w-full rounded-md pl-9 pr-3 text-[16px] outline-none focus-visible:!outline-none focus-visible:ring-2 focus-visible:ring-ring"
          style={{ background: M.card, border: `1px solid ${M.border}`, color: M.ink }}
        />
      </label>
      <div className="flex items-center justify-between">
        <p className="text-[13px] tabular-nums" style={{ color: M.ink3 }}>
          {people.data?.total ?? 0} {t('analytics_people_candidates')}
        </p>
        <button
          type="button"
          onClick={() => filters.setSort(filters.sortBy, filters.sortDir === 'desc' ? 'asc' : 'desc')}
          className="min-h-11 text-[14px] font-medium"
          style={{ color: M.ink2 }}
        >
          {t('sort')} · {filters.sortDir === 'desc' ? '↓' : '↑'}
        </button>
      </div>
      {rows.length === 0 ? (
        <MobileEmpty
          icon={<UsersRound size={22} />}
          title={t('analytics_people_empty')}
          description={filters.q ? t('analytics_clear_search_hint') : undefined}
        />
      ) : (
        <>
          <ListCard>
            {rows.map((person) => (
              <div key={person.candidateId} className="flex items-center gap-3 px-3.5 py-3">
                <Monogram initials={initials(person.name)} size={32} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[15px] font-medium" style={{ color: M.ink }}>
                    {person.name}
                  </span>
                  <span className="block truncate text-[12.5px] tabular-nums" style={{ color: M.ink3 }}>
                    {person.attempts} {t('attempts')} ·{' '}
                    {person.lastActiveAt ? new Date(person.lastActiveAt).toLocaleDateString() : t('no_activity')}
                  </span>
                </span>
                <span className="text-right">
                  <span className="block text-[15px] font-bold tabular-nums" style={{ color: scoreColor(person.avgScore) }}>
                    {person.avgScore}%
                  </span>
                  <span className="text-[12px] tabular-nums" style={{ color: M.ink3 }}>
                    {t('analytics_people_best')} {person.bestScore}%
                  </span>
                </span>
              </div>
            ))}
          </ListCard>
          {totalPages > 1 && (
            <nav className="flex items-center justify-between" aria-label={t('pagination')}>
              <button
                type="button"
                disabled={currentPage <= 1}
                onClick={() => filters.setPage(currentPage - 1)}
                className="min-h-11 px-2 text-[14px] font-medium disabled:opacity-40"
                style={{ color: M.ink }}
              >
                {t('previous')}
              </button>
              <span className="text-[13px] tabular-nums" style={{ color: M.ink3 }}>
                {currentPage} / {totalPages}
              </span>
              <button
                type="button"
                data-mobile-analytics-next-page
                onClick={() => filters.setPage(currentPage + 1)}
                disabled={currentPage >= totalPages}
                className="min-h-11 px-2 text-[14px] font-medium disabled:opacity-40"
                style={{ color: M.ink }}
              >
                {t('next')}
              </button>
            </nav>
          )}
        </>
      )}
    </div>
  );
}

function ItemCards({ data }: { data?: ItemAnalysisResponse }) {
  const { t } = useTranslation();
  if (!data || data.items.length === 0) return <MobileEmpty title={t('analytics_items_empty')} />;
  return (
    <ListCard>
      {data.items.map((item, index) => (
        <div key={item.questionId} className="flex items-center gap-3 px-3.5 py-3">
          <Monogram initials={String(item.order || index + 1)} size={28} />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[14px]" style={{ color: M.ink }}>
              {item.prompt.replace(/<[^>]+>/g, '') || '—'}
            </span>
            <span className="block text-[12.5px] tabular-nums" style={{ color: M.ink3 }}>
              {t('analytics_items_responses')}: {item.responses} · {t('analytics_items_p_value')}: {Math.round(item.pValue * 100)}%
            </span>
          </span>
          {item.flag && (
            <StatusBadge tone={item.flag === 'too_hard' ? 'error' : 'warning'} dot={false}>
              {t(`analytics_items_${item.flag}`)}
            </StatusBadge>
          )}
        </div>
      ))}
    </ListCard>
  );
}

export function TestsTab({
  tests,
  items,
  filters,
}: {
  tests: Query<TestsResponse>;
  items: Query<ItemAnalysisResponse>;
  filters: AnalyticsFilters;
}) {
  const { t } = useTranslation();
  if (tests.isLoading) return <MobileSkeleton rows={5} />;
  if (tests.isError) return <RetryPanel scope="tests" error={tests.error} onRetry={() => void tests.refetch()} />;
  const data = tests.data;
  if (!data || data.tests.length === 0) return <MobileEmpty title={t('no_tests_yet')} />;
  const scored = data.distribution.reduce((sum, bucket) => sum + bucket.count, 0);
  const graded = data.funnel.find((step) => step.stage === 'graded')?.count ?? 0;

  return (
    <div className="space-y-4 px-4 pb-2 pt-3">
      <section className="grid grid-cols-2 gap-2.5">
        <StatTile label={t('analytics_score_distribution')} value={scored} sub={t('analytics_candidates')} />
        <StatTile label={t('completion_funnel')} value={graded} sub={t('analytics_funnel_graded')} />
      </section>
      <section>
        <SectionTitle>{t('test_performance')}</SectionTitle>
        <ListCard>
          {data.tests.map((test) => (
            <button
              key={test.testId}
              type="button"
              aria-pressed={filters.testId === test.testId}
              onClick={() => filters.setTestId(filters.testId === test.testId ? undefined : test.testId)}
              className="flex min-h-[60px] w-full items-center gap-2 px-3.5 py-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
              style={{ background: filters.testId === test.testId ? M.muted : undefined }}
            >
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[15px] font-medium" style={{ color: M.ink }}>
                  {test.title}
                </span>
                <span className="mt-0.5 block text-[12.5px] tabular-nums" style={{ color: M.ink3 }}>
                  {test.total} {t('attempts')} · {test.graded} {t('graded_attempts')}
                </span>
              </span>
              <span className="text-[15px] font-bold tabular-nums" style={{ color: scoreColor(test.avgScore) }}>
                {test.avgScore}%
              </span>
              <ChevronRight size={16} style={{ color: M.chevron }} aria-hidden />
            </button>
          ))}
        </ListCard>
      </section>
      {filters.testId && (
        <section>
          <div className="mb-2 flex items-center justify-between">
            <h2 className="text-[14px] font-semibold" style={{ color: M.ink }}>
              {t('analytics_items_heading')}
            </h2>
            <button
              type="button"
              onClick={() => filters.setTestId(undefined)}
              className="min-h-11 text-[14px] font-medium"
              style={{ color: M.ink2 }}
            >
              {t('analytics_items_clear')}
            </button>
          </div>
          {items.isLoading ? (
            <MobileSkeleton rows={3} />
          ) : items.isError ? (
            <RetryPanel scope="items" error={items.error} onRetry={() => void items.refetch()} />
          ) : (
            <ItemCards data={items.data} />
          )}
        </section>
      )}
    </div>
  );
}

const RISK_TONE = { high: 'error', medium: 'warning', low: 'success' } as const;

export function IntegrityTab({ integrity }: { integrity: Query<IntegrityResponse> }) {
  const { t } = useTranslation();
  if (integrity.isLoading) return <MobileSkeleton rows={4} />;
  if (integrity.isError) {
    return <RetryPanel scope="integrity" error={integrity.error} onRetry={() => void integrity.refetch()} />;
  }
  const data = integrity.data;
  if (!data || data.totalAttempts === 0) {
    return <MobileEmpty icon={<ShieldAlert size={22} />} title={t('analytics_integrity_empty')} />;
  }

  return (
    <div className="space-y-4 px-4 pb-2 pt-3">
      <section className="grid grid-cols-2 gap-2.5">
        <StatTile
          label={t('analytics_integrity_monitored')}
          value={`${data.monitoredAttempts}/${data.totalAttempts}`}
          sub={t('analytics_integrity_attempts')}
        />
        <StatTile
          label={t('analytics_integrity_flagged_rate')}
          value={`${data.flaggedRate}%`}
          tone={data.riskBands.high > 0 ? 'error' : undefined}
          sub={`${data.riskBands.high} ${t('analytics_risk_high')}`}
        />
      </section>
      <section>
        <SectionTitle>{t('analytics_integrity_events')}</SectionTitle>
        {data.byType.length === 0 ? (
          <p className="text-[13px]" style={{ color: M.ink3 }}>
            {t('analytics_integrity_no_events')}
          </p>
        ) : (
          <ListCard>
            {data.byType.map((event) => (
              <div key={event.type} className="flex items-center gap-3 px-3.5 py-3">
                <span className="min-w-0 flex-1 truncate text-[14px]" style={{ color: M.ink2 }}>
                  {t(`analytics_event_${event.type.toLowerCase()}`)}
                </span>
                <span className="text-[14px] font-semibold tabular-nums" style={{ color: M.ink }}>
                  {event.count}
                </span>
              </div>
            ))}
          </ListCard>
        )}
      </section>
      <section>
        <SectionTitle>{t('analytics_integrity_top_risk')}</SectionTitle>
        {data.topRiskAttempts.length === 0 ? (
          <MobileEmpty title={t('analytics_integrity_no_flags')} />
        ) : (
          <ListCard>
            {data.topRiskAttempts.map((attempt) => (
              <div key={attempt.attemptId} className="flex items-center gap-3 px-3.5 py-3">
                <Monogram initials={initials(attempt.candidateName)} size={32} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[15px] font-medium" style={{ color: M.ink }}>
                    {attempt.candidateName}
                  </span>
                  <span className="block truncate text-[12.5px]" style={{ color: M.ink3 }}>
                    {attempt.testTitle}
                  </span>
                </span>
                <span className="flex flex-col items-end gap-1">
                  <StatusBadge tone={RISK_TONE[attempt.band]} dot={false}>
                    {t(`analytics_risk_${attempt.band}`)} · {attempt.riskScore}
                  </StatusBadge>
                  <span className="text-[12px] tabular-nums" style={{ color: M.ink3 }}>
                    {attempt.score == null ? '—' : `${attempt.score}%`}
                  </span>
                </span>
              </div>
            ))}
          </ListCard>
        )}
      </section>
    </div>
  );
}
